import React, { useState, useEffect } from 'react';
import { X, FolderInput, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { api } from '../../api/client';

// Generic "move to a different workshop" modal. Works for both foresight and
// writing sessions -- the caller supplies `onMove`, which should call
// api.moveSession or api.moveWritingSession and return the response.
//
// `linkedFieldKey` names the field in the response that reports an
// auto-cleared cross-link (e.g. 'unlinked_foresight_session' for a writing
// session move, 'unlinked_writing_sessions' for a foresight session move --
// the latter is an array since one foresight session can have many linked
// writing sessions).
const MoveToWorkshopModal = ({ itemName, currentWorkshopId, onMove, onClose, onMoved }) => {
  const [workshops, setWorkshops] = useState([]);
  const [selectedWorkshopId, setSelectedWorkshopId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isMoving, setIsMoving] = useState(false);
  const [result, setResult] = useState(null); // set after a successful move

  useEffect(() => {
    (async () => {
      try {
        const data = await api.getWorkshops();
        setWorkshops(data.filter(w => w.id !== currentWorkshopId));
      } catch (error) {
        console.error('Failed to load workshops:', error);
      } finally {
        setIsLoading(false);
      }
    })();
  }, [currentWorkshopId]);

  const handleMove = async () => {
    if (!selectedWorkshopId) return;
    setIsMoving(true);
    try {
      const response = await onMove(parseInt(selectedWorkshopId));
      setResult(response);
    } catch (error) {
      alert('Failed to move: ' + error.message);
    } finally {
      setIsMoving(false);
    }
  };

  const handleDone = () => {
    onMoved && onMoved();
    onClose();
  };

  // Normalize whichever "was something unlinked" shape the response has into
  // a simple list of { id, name } to display, or null if nothing was cleared.
  const clearedLinks = (() => {
    if (!result) return null;
    if (result.unlinked_foresight_session) return [result.unlinked_foresight_session];
    if (result.unlinked_writing_sessions && result.unlinked_writing_sessions.length > 0) {
      return result.unlinked_writing_sessions;
    }
    return null;
  })();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <FolderInput className="w-5 h-5 text-[#01006A]" />
            Move to Workshop
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6">
          {!result ? (
            <>
              <p className="text-sm text-gray-600 mb-4">
                Move <span className="font-medium text-gray-900">"{itemName}"</span> to a different workshop.
                Nothing about its content -- sections, contributions, takeaways -- is affected.
              </p>

              {isLoading ? (
                <p className="text-sm text-gray-400">Loading workshops…</p>
              ) : workshops.length === 0 ? (
                <p className="text-sm text-gray-500">There are no other workshops to move this into yet.</p>
              ) : (
                <select
                  value={selectedWorkshopId}
                  onChange={e => setSelectedWorkshopId(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#01006A] mb-2"
                >
                  <option value="">Select destination workshop…</option>
                  {workshops.map(w => (
                    <option key={w.id} value={w.id}>{w.name}</option>
                  ))}
                </select>
              )}

              <p className="text-xs text-gray-400 mb-5">
                If this item is linked to something in its current workshop that doesn't move with it,
                that link will be cleared automatically -- you'll see exactly what changed before you're done here.
              </p>

              <div className="flex gap-3">
                <button onClick={onClose}
                  className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium">
                  Cancel
                </button>
                <button onClick={handleMove} disabled={!selectedWorkshopId || isMoving}
                  className="flex-1 px-4 py-2 bg-[#01006A] text-white rounded-lg hover:bg-[#010059] disabled:bg-gray-300 text-sm font-medium">
                  {isMoving ? 'Moving…' : 'Move'}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4">
                <CheckCircle2 className="w-5 h-5 flex-shrink-0" />
                <p className="text-sm font-medium">Moved successfully. All content is intact.</p>
              </div>

              {clearedLinks && (
                <div className="flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-4">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">
                    {clearedLinks.length === 1 && !result.unlinked_writing_sessions ? (
                      <>Its link to the foresight session <span className="font-medium">"{clearedLinks[0].name}"</span> was cleared, since that session stayed in the original workshop. Nothing else changed.</>
                    ) : (
                      <>The following writing session(s) were unlinked from this foresight session, since they're staying in the original workshop: {clearedLinks.map(l => `"${l.name}"`).join(', ')}. Their content is untouched -- only the link was cleared.</>
                    )}
                  </p>
                </div>
              )}

              <button onClick={handleDone}
                className="w-full px-4 py-2 bg-[#01006A] text-white rounded-lg hover:bg-[#010059] text-sm font-medium">
                Done
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default MoveToWorkshopModal;
